import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Form, Button, Spinner, Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import HtmlHead from 'components/html-head/HtmlHead';
import CsLineIcons from 'cs-line-icons/CsLineIcons';
import { ToastSuccess, ToastErrorApi, ToastErrorCath } from 'components/alerts/toast';
import { getNotificationsConfig, updateNotificationsConfig } from 'api/Api-user';

const TRACKING_STATUSES = [
  'Etiqueta creada',
  'En camino',
  'Proceso de Entrega',
  'Entregado',
  'Excepción',
  'Entrega actualizada',
  'Retornado',
  'Recolectado',
];

const Notifications = () => {
  const title = 'Notificaciones';
  const description = 'Notificaciones Plataforma Ioenvio';

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTogglingEnabled, setIsTogglingEnabled] = useState(false);
  const [isConfigured, setIsConfigured] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingToggleValue, setPendingToggleValue] = useState(null);

  const [emailCompanyName, setEmailCompanyName] = useState('');
  const [destEnabled, setDestEnabled] = useState(false);
  const [destStatuses, setDestStatuses] = useState([]);
  const [destWhatsapp, setDestWhatsapp] = useState(false);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await getNotificationsConfig();
        if (response.destination_notification_statuses !== undefined) {
          const name = response.email_company_name || '';
          const statuses = response.destination_notification_statuses || [];
          const whatsapp = (response.destination_channels || []).includes('WHATSAPP');
          const enabled = response.destination_notifications_enabled || false;
          setEmailCompanyName(name);
          setDestEnabled(enabled);
          setDestStatuses(statuses);
          setDestWhatsapp(whatsapp);
          if (name || statuses.length > 0 || whatsapp || enabled) {
            setIsConfigured(true);
          }
        } else if (response.error) {
          const firstKey = Object.keys(response.error)[0];
          ToastErrorApi(response.error[firstKey]);
        }
      } catch (err) {
        ToastErrorCath();
      } finally {
        setIsLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const toggleStatus = (status) => {
    setDestStatuses((prev) => (prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]));
  };

  const handleToggleEnabled = async (value) => {
    setDestEnabled(value);
    setIsTogglingEnabled(true);
    try {
      const response = await updateNotificationsConfig({ destination_notifications_enabled: value });
      if (response.destination_notification_statuses !== undefined) {
        ToastSuccess(value ? 'Notificaciones activadas' : 'Notificaciones desactivadas');
      } else if (response.error) {
        setDestEnabled(!value);
        const firstKey = Object.keys(response.error)[0];
        ToastErrorApi(response.error[firstKey]);
      }
    } catch (err) {
      setDestEnabled(!value);
      ToastErrorCath();
    } finally {
      setIsTogglingEnabled(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const payload = {
        email_company_name: emailCompanyName,
        destination_notification_statuses: destStatuses,
        destination_channels: destWhatsapp ? ['WHATSAPP'] : [],
        ...(!isConfigured && { destination_notifications_enabled: true }),
      };
      const response = await updateNotificationsConfig(payload);
      if (response.destination_notification_statuses !== undefined) {
        if (!isConfigured) setDestEnabled(true);
        setIsConfigured(true);
        ToastSuccess('Configuración actualizada correctamente');
      } else if (response.error) {
        const firstKey = Object.keys(response.error)[0];
        ToastErrorApi(response.error[firstKey]);
      }
    } catch (err) {
      ToastErrorCath();
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <>
      <HtmlHead title={title} description={description} />
      <div className="page-title-container mb-3">
        <Row className="g-0">
          <Col className="col-auto mb-3 mb-sm-0 me-auto">
            <h1 className="mb-0 pb-0 display-4">{title}</h1>
          </Col>
        </Row>
      </div>

      <Row>
        <Col xs="12" lg="8">
          <div className="d-flex align-items-center gap-2 justify-content-end mb-3">
          <span className="text-muted text-small">Estado</span>
          <span
            style={{
              backgroundColor: destEnabled ? '#388e3c' : '#9e9e9e',
              color: '#fff',
              borderRadius: '50px',
              padding: '4px 12px',
              fontSize: '11px',
              fontWeight: '600',
              display: 'inline-block',
              opacity: isTogglingEnabled ? 0.7 : 1,
            }}
          >
            {isTogglingEnabled && <Spinner as="span" animation="border" size="sm" />}
            {!isTogglingEnabled && (destEnabled ? 'ACTIVAS' : 'INACTIVAS')}
          </span>
          <Form.Check
            type="switch"
            id="dest-enabled-switch"
            checked={destEnabled}
            disabled={!isConfigured || isTogglingEnabled}
            onChange={(e) => { setPendingToggleValue(e.target.checked); setShowConfirmModal(true); }}
            className="mb-0"
          />
          <OverlayTrigger
            placement="right"
            overlay={
              <Tooltip>
                {isConfigured
                  ? 'Haz clic para activar o desactivar las notificaciones'
                  : 'Completá la configuración antes de activar las notificaciones'}
              </Tooltip>
            }
          >
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                border: '1px solid #9e9e9e',
                fontSize: '10px',
                color: '#9e9e9e',
                cursor: 'help',
                flexShrink: 0,
              }}
            >
              ?
            </span>
          </OverlayTrigger>
          </div>
          <Card className="mb-4">
            <Card.Body>
              <h5 className="mb-1">Editar notificaciones para tus clientes</h5>
              <p className="text-muted text-small mb-4">
                Las notificaciones para tus clientes están {destEnabled ? 'activas' : 'inactivas'}. Podés enviar alertas de seguimiento a tus clientes.
              </p>

              <Form.Group className="mb-4">
                <Form.Label className="text-muted text-small">1. Nombre de tu empresa o negocio</Form.Label>
                <div className="input-group">
                  <span className="input-group-text bg-transparent">
                    <CsLineIcons icon="building" size="16" />
                  </span>
                  <Form.Control
                    type="text"
                    value={emailCompanyName}
                    onChange={(e) => setEmailCompanyName(e.target.value)}
                    placeholder="Nombre de tu empresa"
                  />
                </div>
                <Form.Text className="text-muted">Este nombre aparecerá como remitente en las notificaciones que se envíen a tus clientes.</Form.Text>
              </Form.Group>

              <Form.Group className="mb-4">
                <Form.Label className="text-muted text-small">2. Selecciona los estados de seguimiento para los cuales deseas enviar notificaciones:</Form.Label>
                {TRACKING_STATUSES.map((status) => (
                  <Form.Check
                    key={status}
                    type="checkbox"
                    id={`dest-status-${status}`}
                    label={status}
                    checked={destStatuses.includes(status)}
                    onChange={() => toggleStatus(status)}
                    className="mb-1"
                  />
                ))}
              </Form.Group>

              <Form.Group className="mb-2">
                <Form.Label className="text-muted text-small">3. Activar notificaciones por WhatsApp:</Form.Label>
                <Form.Check
                  type="checkbox"
                  id="dest-whatsapp-check"
                  label="Sí"
                  checked={destWhatsapp}
                  onChange={(e) => setDestWhatsapp(e.target.checked)}
                />
              </Form.Group>
            </Card.Body>
          </Card>

          <div className="mb-5">
            <Button variant="primary" onClick={handleSave} disabled={isSaving}>
              {isSaving && (
                <>
                  <Spinner as="span" animation="border" size="sm" className="me-2" />
                  Guardando...
                </>
              )}
              {!isSaving && (isConfigured ? 'Actualizar configuración' : 'Actualizar y activar')}
            </Button>
          </div>
        </Col>
      </Row>

      <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
        <Modal.Header closeButton />
        <Modal.Body className="py-4 px-4">
          <p className="mb-0 fs-5">
            ¿Confirmas que deseas <span className="text-fake-bold">{pendingToggleValue ? 'activar' : 'desactivar'}</span> las notificaciones de destinatario?
          </p>
        </Modal.Body>
        <Modal.Footer className="border-top">
          <Button variant="outline-primary" onClick={() => setShowConfirmModal(false)}>
            Cancelar
          </Button>
          <Button
            variant="primary"
            disabled={isTogglingEnabled}
            onClick={() => { setShowConfirmModal(false); handleToggleEnabled(pendingToggleValue); }}
          >
            {isTogglingEnabled ? <Spinner as="span" animation="border" size="sm" /> : 'Sí, continuar'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Notifications;
